import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import DashboardOverviewPage from '@/pages/DashboardOverviewPage';
import ClientsPage from '@/pages/ClientsPage';
import ClientDetailPage from '@/pages/ClientDetailPage';
import PropertiesPage from '@/pages/PropertiesPage';
import AppraisalsPage from '@/pages/AppraisalsPage';
import AppraisalDetailPage from '@/pages/AppraisalDetailPage';
import WalkthroughsPage from '@/pages/WalkthroughsPage';
import LeadsPage from '@/pages/LeadsPage';
import WebLeadsPage from '@/pages/WebLeadsPage';
import LeadDetailPage from '@/pages/LeadDetailPage';
import RoofTemplatesPage from '@/pages/RoofTemplatesPage';
import ProcedureTemplatesPage from '@/pages/ProcedureTemplatesPage';
import RoofM2CalibrationPage from '@/pages/RoofM2CalibrationPage';
import StockBulkInPage from '@/pages/StockBulkInPage';
import JobReturnPage from '@/pages/JobReturnPage';
import FlashingTemplatesPage from '@/pages/FlashingTemplatesPage';
import PipelineHubPage from '@/pages/PipelineHubPage';
import StageDetailPage from '@/pages/StageDetailPage';
import DocumentsSearchPage from '@/pages/DocumentsSearchPage';
import PurlinCalculatorPage from '@/pages/PurlinCalculatorPage';
import PrintStackPage from '@/pages/PrintStackPage';
import ContractorsPage from '@/pages/ContractorsPage';
import ContractorReportsPage from '@/pages/ContractorReportsPage';
import RoofDetailsDropdownsAdmin from '@/pages/RoofDetailsDropdownsAdmin';
import SpoutingConsumablesAdmin from '@/pages/SpoutingConsumablesAdmin';
import FlashingPipeMappingAdmin from '@/pages/FlashingPipeMappingAdmin';
import RemedialRepairPresetsAdmin from '@/pages/RemedialRepairPresetsAdmin';
import ComponentLabourRatesPage from '@/pages/ComponentLabourRatesPage';
import PendingApprovalsPage from '@/pages/PendingApprovalsPage';
import UserManagementPage from '@/pages/UserManagementPage';
import ProfilePage from '@/pages/ProfilePage';
import GoogleDrivePage from '@/pages/GoogleDrivePage';
import SecuritySettingsPage from '@/pages/SecuritySettingsPage';
import InsurancePage from '@/pages/InsurancePage';
import DataMigrationPage from '@/pages/DataMigrationPage';
import DataSyncPage from '@/pages/DataSyncPage';
import ProductCatalogPage from '@/pages/ProductCatalogPage';
import PropertyDetailPage from '@/pages/PropertyDetailPage';
import SuppliersPage from '@/pages/SuppliersPage';
import TelemarketingPage from '@/pages/TelemarketingPage';
import LetterTemplatesPage from '@/pages/LetterTemplatesPage';
import ReportsLibraryPage from '@/pages/ReportsLibraryPage';
import CalendarPage from '@/pages/CalendarPage';
import CompanySettingsPage from '@/pages/CompanySettingsPage';
import PdfFilenameTemplatesAdminPage from '@/pages/PdfFilenameTemplatesAdminPage';
import DocumentsDriveAdminPage from '@/pages/DocumentsDriveAdminPage';
import JobModelAdminPage from '@/pages/JobModelAdminPage';
import UpdateResendPage from '@/pages/UpdateResendPage';
import ReconnectPage from '@/pages/ReconnectPage';
import ReconnectPublicPage from '@/pages/ReconnectPublicPage';
import MaintenancePlansPage from '@/pages/MaintenancePlansPage';
import MaintenanceVisitPublicPage from '@/pages/MaintenanceVisitPublicPage';
import QuoteAcceptancePublicPage from '@/pages/QuoteAcceptancePublicPage';
import DayPlannerPage from '@/pages/DayPlannerPage';
import DaySheetPage from '@/pages/DaySheetPage';
import VariantInsightsPage from '@/pages/VariantInsightsPage';
import StatusWorkflowsPage from '@/pages/StatusWorkflowsPage';
import WorkflowStartPage from '@/pages/workflow/WorkflowStartPage';
import WorkflowRunnerPage from '@/pages/workflow/WorkflowRunnerPage';
import WorkflowConsolePage from '@/pages/workflow/WorkflowConsolePage';
import WorkflowSplitPage from '@/pages/workflow/WorkflowSplitPage';
import MobileAppraisalPage from '@/pages/MobileAppraisalPage';
import ForemanMobilePage from '@/pages/ForemanMobilePage';
import InventoryPage from '@/pages/InventoryPage';
import FinancialDashboardPage from '@/pages/FinancialDashboardPage';
import GanttChartPage from '@/pages/GanttChartPage';
import SchedulePage from '@/pages/SchedulePage';
import ClientPortalPage from '@/pages/ClientPortalPage';
import AuditLogPage from '@/pages/AuditLogPage';
import BulkOperationsPage from '@/pages/BulkOperationsPage';
import DescriptionEditorPage from '@/pages/DescriptionEditorPage';
import ItemRequirementsPage from '@/pages/ItemRequirementsPage';
import RoofTypeMaterialsPage from '@/pages/RoofTypeMaterialsPage';
import ProductRoofTypesPage from '@/pages/ProductRoofTypesPage';
import DropdownManagerPage from '@/pages/DropdownManagerPage';
import ReportSetupPage from '@/pages/ReportSetupPage';
// EstimateRatesPage retired 2026-02 (see Estimate Templates)
import PdfViewerPage from '@/pages/PdfViewerPage';
import RoofConditionsPage from '@/pages/RoofConditionsPage';
import InvoicesPage from '@/pages/InvoicesPage';
import InvoiceDetailPage from '@/pages/InvoiceDetailPage';
import ExpensesPage from '@/pages/ExpensesPage';
import UserManualPage from '@/pages/UserManualPage';
import FranchiseManagerPage from '@/pages/FranchiseManagerPage';
import TaskCatalogPage from '@/pages/TaskCatalogPage';
// Sprint 2 Order 4 (2026-09-14) — PurchaseOrdersPage retired.
import FreeAppraisalFormPage from '@/pages/FreeAppraisalFormPage';
import ComponentRateTablesPage from '@/pages/ComponentRateTablesPage';
import CatalogueAuditPage from '@/pages/CatalogueAuditPage';
import RecipeEditorPage from '@/pages/RecipeEditorPage';
import LabourCostModelAdminPage from '@/pages/LabourCostModelAdminPage';
import MaterialsAuditPage from '@/pages/MaterialsAuditPage';
import SuppliersAdminPage from '@/pages/SuppliersAdminPage';
import OrphanSweepPage from '@/pages/OrphanSweepPage';
import MigrationsPage from '@/pages/MigrationsPage';
import BulletGroupsPage from '@/pages/BulletGroupsPage';
import FixingsPage from '@/pages/FixingsPage';
import QuoteMathsCheckPage from '@/pages/QuoteMathsCheckPage';
import TestDataCleanupPage from '@/pages/TestDataCleanupPage';
import PropertyDuplicatesAuditPage from '@/pages/PropertyDuplicatesAuditPage';
import SuburbAreaCodesAdminPage from '@/pages/SuburbAreaCodesAdminPage';
import IdChipThemeAdminPage from '@/pages/IdChipThemeAdminPage';
import ExtraPaintingAdminPage from '@/pages/ExtraPaintingAdminPage';
import PaintSystemsAdminPage from '@/pages/PaintSystemsAdminPage';
// iter303ak-fix23f · Editable heuristic buckets for Accepted T10 A15
import ApproxStartHeuristicsAdminPage from '@/pages/ApproxStartHeuristicsAdminPage';
import RulesEngineAdminPage from '@/pages/RulesEngineAdminPage';
import { IdChipThemeProvider } from '@/contexts/IdChipThemeContext';
import AppraisalConditionsPage from '@/pages/AppraisalConditionsPage';
import RoofReportPage from '@/pages/RoofReportPage';
import TerritoryListsPage from '@/pages/TerritoryListsPage';
import RoofHealthCheckPage from '@/pages/RoofHealthCheckPage';
import ProductionNotesPage from '@/pages/ProductionNotesPage';
import AgentsPage from '@/pages/AgentsPage';
import CommunicationScriptsPage from '@/pages/CommunicationScriptsPage';
import ConditionScriptsPage from '@/pages/ConditionScriptsPage';
import ConditionScriptMatrixPage from '@/pages/ConditionScriptMatrixPage';
import ScriptAuditPage from '@/pages/ScriptAuditPage';
import QuoteTypesManagePage from '@/pages/QuoteTypesManagePage';
import TokenRegistryPage from '@/pages/TokenRegistryPage';
import DuplicateClientsPage from '@/pages/DuplicateClientsPage';
import EstimateTemplatesPage from '@/pages/EstimateTemplatesPage';
import AppLayout from '@/components/layout/AppLayout';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
      <div className="text-center">
        <div className="w-10 h-10 rounded-md bg-[#D35400] flex items-center justify-center mx-auto mb-3 animate-pulse"><span className="text-white text-lg font-extrabold">R</span></div>
        <p className="text-sm text-slate-400">Loading...</p>
      </div>
    </div>
  );
  if (user === false) return <Navigate to="/login" />;
  return <AppLayout>{children}</AppLayout>;
}

function MobileRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]"><div className="animate-pulse text-slate-400">Loading...</div></div>;
  if (user === false) return <Navigate to="/login" />;
  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  // Iter276-length — bootstrap the stick-rule override once we have an
  // authenticated user. Pin annotator / Components tab read the
  // module-level `IRON_RIDGE_MAX_M` / `IRON_RIDGE_LAP_M` at call time,
  // so this single fetch updates every downstream consumer without a
  // prop drill. Failure is non-fatal — defaults stand.
  React.useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const mod = await import('@/components/annotator/constants');
        const token = localStorage.getItem('access_token');
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/stick-rule-defaults`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        mod.applyStickRule?.(data?.defaults || {});
      } catch (e) {
        // Non-fatal: stick rule keeps its hard-coded defaults.
        console.warn('Stick-rule bootstrap failed:', e?.message);
      }
    })();
  }, [user]);
  if (loading) return null;
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/overview" element={<ProtectedRoute><DashboardOverviewPage /></ProtectedRoute>} />
      <Route path="/command-centre" element={<ProtectedRoute><DashboardOverviewPage /></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><ClientsPage /></ProtectedRoute>} />
      <Route path="/clients/:id" element={<ProtectedRoute><ClientDetailPage /></ProtectedRoute>} />
      <Route path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />
      <Route path="/properties/:id" element={<ProtectedRoute><PropertyDetailPage /></ProtectedRoute>} />
      <Route path="/appraisals" element={<ProtectedRoute><AppraisalsPage /></ProtectedRoute>} />
      <Route path="/walkthroughs" element={<ProtectedRoute><WalkthroughsPage /></ProtectedRoute>} />
      <Route path="/appraisals/:id" element={<ProtectedRoute><AppraisalDetailPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentsSearchPage /></ProtectedRoute>} />
      <Route path="/appraisals/:id/reroof-specs/:specId/purlin-calculator" element={<ProtectedRoute><PurlinCalculatorPage /></ProtectedRoute>} />
      <Route path="/appraisals/:id/mobile" element={<MobileRoute><MobileAppraisalPage /></MobileRoute>} />
      <Route path="/m/job/:id" element={<MobileRoute><ForemanMobilePage /></MobileRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/web-leads" element={<ProtectedRoute><WebLeadsPage /></ProtectedRoute>} />
      <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />
      <Route path="/contractors" element={<ProtectedRoute><ContractorsPage /></ProtectedRoute>} />
      <Route path="/contractor-reports" element={<ProtectedRoute><ContractorReportsPage /></ProtectedRoute>} />
      <Route path="/admin/roof-details-dropdowns" element={<ProtectedRoute><RoofDetailsDropdownsAdmin /></ProtectedRoute>} />
      <Route path="/admin/quote-maths-check" element={<ProtectedRoute><QuoteMathsCheckPage /></ProtectedRoute>} />
      <Route path="/admin/migrations" element={<ProtectedRoute><MigrationsPage /></ProtectedRoute>} />
      <Route path="/admin/bullet-groups" element={<ProtectedRoute><BulletGroupsPage /></ProtectedRoute>} />
      <Route path="/admin/fixings" element={<ProtectedRoute><FixingsPage /></ProtectedRoute>} />
      <Route path="/admin/orphan-sweep" element={<ProtectedRoute><OrphanSweepPage /></ProtectedRoute>} />
      <Route path="/admin/test-data-cleanup" element={<ProtectedRoute><TestDataCleanupPage /></ProtectedRoute>} />
      <Route path="/admin/property-duplicates" element={<ProtectedRoute><PropertyDuplicatesAuditPage /></ProtectedRoute>} />
      <Route path="/admin/suburb-area-codes" element={<ProtectedRoute><SuburbAreaCodesAdminPage /></ProtectedRoute>} />
      <Route path="/admin/id-chip-theme" element={<ProtectedRoute><IdChipThemeAdminPage /></ProtectedRoute>} />
      <Route path="/update-resend" element={<ProtectedRoute><UpdateResendPage /></ProtectedRoute>} />
      <Route path="/reconnect" element={<ProtectedRoute><ReconnectPage /></ProtectedRoute>} />
      <Route path="/reconnect/:token" element={<ReconnectPublicPage />} />
      <Route path="/maintenance-plans" element={<ProtectedRoute><MaintenancePlansPage /></ProtectedRoute>} />
      <Route path="/maintenance-visit/:token" element={<MaintenanceVisitPublicPage />} />
      <Route path="/accept/:token" element={<QuoteAcceptancePublicPage />} />
      <Route path="/day-planner" element={<ProtectedRoute><DayPlannerPage /></ProtectedRoute>} />
      <Route path="/day-planner/:date/sheet" element={<ProtectedRoute><DaySheetPage /></ProtectedRoute>} />
      <Route path="/admin/job-model" element={<ProtectedRoute><JobModelAdminPage /></ProtectedRoute>} />
      <Route path="/reports/variant-insights" element={<ProtectedRoute><VariantInsightsPage /></ProtectedRoute>} />
      <Route path="/admin/documents-drive" element={<ProtectedRoute><DocumentsDriveAdminPage /></ProtectedRoute>} />
      <Route path="/admin/pdf-filename-templates" element={<ProtectedRoute><PdfFilenameTemplatesAdminPage /></ProtectedRoute>} />
      <Route path="/admin/extra-painting-items" element={<ProtectedRoute><ExtraPaintingAdminPage /></ProtectedRoute>} />
      <Route path="/admin/paint-systems" element={<ProtectedRoute><PaintSystemsAdminPage /></ProtectedRoute>} />
      {/* iter303ak-fix23f · Approximate start-time heuristic buckets + rules */}
      <Route path="/admin/approx-start-heuristics" element={<ProtectedRoute><ApproxStartHeuristicsAdminPage /></ProtectedRoute>} />
      <Route path="/admin/rules-engine" element={<ProtectedRoute><RulesEngineAdminPage /></ProtectedRoute>} />
      <Route path="/admin/roof-m2-calibration" element={<ProtectedRoute><RoofM2CalibrationPage /></ProtectedRoute>} />
      <Route path="/admin/stock-bulk-in" element={<ProtectedRoute><StockBulkInPage /></ProtectedRoute>} />
      <Route path="/admin/job-return/:appraisalId" element={<ProtectedRoute><JobReturnPage /></ProtectedRoute>} />
      <Route path="/admin/job-return" element={<ProtectedRoute><JobReturnPage /></ProtectedRoute>} />
      <Route path="/admin/spouting-consumables" element={<ProtectedRoute><SpoutingConsumablesAdmin /></ProtectedRoute>} />
      <Route path="/admin/flashing-pipe-mapping" element={<ProtectedRoute><FlashingPipeMappingAdmin /></ProtectedRoute>} />
      <Route path="/admin/repair-presets" element={<ProtectedRoute><RemedialRepairPresetsAdmin /></ProtectedRoute>} />
      <Route path="/admin/component-labour-rates" element={<ProtectedRoute><ComponentLabourRatesPage /></ProtectedRoute>} />
      <Route path="/admin/approvals" element={<ProtectedRoute><PendingApprovalsPage /></ProtectedRoute>} />
      <Route path="/insurance" element={<ProtectedRoute><InsurancePage /></ProtectedRoute>} />
      <Route path="/telemarketing" element={<ProtectedRoute><TelemarketingPage /></ProtectedRoute>} />
      <Route path="/territory" element={<ProtectedRoute><TerritoryListsPage /></ProtectedRoute>} />
      <Route path="/health-check" element={<ProtectedRoute><RoofHealthCheckPage /></ProtectedRoute>} />
      <Route path="/letters" element={<ProtectedRoute><LetterTemplatesPage /></ProtectedRoute>} />
      <Route path="/reports-library" element={<ProtectedRoute><ReportsLibraryPage /></ProtectedRoute>} />
      <Route path="/roof-templates" element={<ProtectedRoute><RoofTemplatesPage /></ProtectedRoute>} />
      <Route path="/procedure-templates" element={<ProtectedRoute><ProcedureTemplatesPage /></ProtectedRoute>} />
      <Route path="/flashing-templates" element={<ProtectedRoute><FlashingTemplatesPage /></ProtectedRoute>} />
      <Route path="/pipeline" element={<ProtectedRoute><PipelineHubPage /></ProtectedRoute>} />
      <Route path="/pipeline/stage/:stageId" element={<ProtectedRoute><StageDetailPage /></ProtectedRoute>} />
      <Route path="/print-stack" element={<ProtectedRoute><PrintStackPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><CompanySettingsPage /></ProtectedRoute>} />
      <Route path="/settings/status-workflows" element={<ProtectedRoute><StatusWorkflowsPage /></ProtectedRoute>} />
      <Route path="/workflow/start" element={<ProtectedRoute><WorkflowStartPage /></ProtectedRoute>} />
      <Route path="/workflow/split" element={<ProtectedRoute><WorkflowSplitPage /></ProtectedRoute>} />
      <Route path="/workflow/appraisal/:appraisalId" element={<ProtectedRoute><WorkflowConsolePage /></ProtectedRoute>} />
      <Route path="/workflow/:runId" element={<ProtectedRoute><WorkflowRunnerPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/financials" element={<ProtectedRoute><FinancialDashboardPage /></ProtectedRoute>} />
      <Route path="/gantt" element={<ProtectedRoute><GanttChartPage /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/audit-log" element={<ProtectedRoute><AuditLogPage /></ProtectedRoute>} />
      <Route path="/bulk" element={<ProtectedRoute><BulkOperationsPage /></ProtectedRoute>} />
      <Route path="/portal" element={<ClientPortalPage />} />
      <Route path="/users" element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/admin/google-drive" element={<ProtectedRoute><GoogleDrivePage /></ProtectedRoute>} />
      <Route path="/admin/security" element={<ProtectedRoute><SecuritySettingsPage /></ProtectedRoute>} />
      <Route path="/migration" element={<ProtectedRoute><DataMigrationPage /></ProtectedRoute>} />
      <Route path="/data-sync" element={<ProtectedRoute><DataSyncPage /></ProtectedRoute>} />
      <Route path="/materials" element={<ProtectedRoute><ProductCatalogPage /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><SuppliersPage /></ProtectedRoute>} />
      <Route path="/descriptions" element={<ProtectedRoute><DescriptionEditorPage /></ProtectedRoute>} />
      <Route path="/item-requirements" element={<ProtectedRoute><ItemRequirementsPage /></ProtectedRoute>} />
      <Route path="/roof-type-materials" element={<ProtectedRoute><RoofTypeMaterialsPage /></ProtectedRoute>} />
      <Route path="/product-roof-types" element={<ProtectedRoute><ProductRoofTypesPage /></ProtectedRoute>} />
      <Route path="/dropdown-manager" element={<ProtectedRoute><DropdownManagerPage /></ProtectedRoute>} />
      <Route path="/report-setup" element={<ProtectedRoute><ReportSetupPage /></ProtectedRoute>} />
      {/* /estimate-rates retired 2026-02 — use /estimate-templates */}
      <Route path="/roof-conditions" element={<ProtectedRoute><RoofConditionsPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><InvoicesPage /></ProtectedRoute>} />
      <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceDetailPage /></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
      <Route path="/user-manual" element={<ProtectedRoute><UserManualPage /></ProtectedRoute>} />
      <Route path="/franchise-manager" element={<ProtectedRoute><FranchiseManagerPage /></ProtectedRoute>} />
      <Route path="/task-catalog" element={<ProtectedRoute><TaskCatalogPage /></ProtectedRoute>} />
      <Route path="/free-appraisal" element={<ProtectedRoute><FreeAppraisalFormPage /></ProtectedRoute>} />
      <Route path="/free-appraisal/:leadId" element={<ProtectedRoute><FreeAppraisalFormPage /></ProtectedRoute>} />
      <Route path="/component-rates" element={<ProtectedRoute><ComponentRateTablesPage /></ProtectedRoute>} />
      <Route path="/catalogue-audit" element={<ProtectedRoute><CatalogueAuditPage /></ProtectedRoute>} />
      <Route path="/catalogue-audit/:component" element={<ProtectedRoute><CatalogueAuditPage /></ProtectedRoute>} />
      <Route path="/recipe-editor/:rowId" element={<ProtectedRoute><RecipeEditorPage /></ProtectedRoute>} />
      <Route path="/admin/labour-cost-model" element={<ProtectedRoute><LabourCostModelAdminPage /></ProtectedRoute>} />
      <Route path="/admin/materials-audit" element={<ProtectedRoute><MaterialsAuditPage /></ProtectedRoute>} />
      <Route path="/admin/suppliers" element={<ProtectedRoute><SuppliersAdminPage /></ProtectedRoute>} />
      <Route path="/appraisal-conditions" element={<ProtectedRoute><AppraisalConditionsPage /></ProtectedRoute>} />
      <Route path="/roof-report" element={<ProtectedRoute><RoofReportPage /></ProtectedRoute>} />
      <Route path="/production-notes" element={<ProtectedRoute><ProductionNotesPage /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute><AgentsPage /></ProtectedRoute>} />
      <Route path="/communication-scripts" element={<ProtectedRoute><CommunicationScriptsPage /></ProtectedRoute>} />
      <Route path="/condition-scripts" element={<ProtectedRoute><ConditionScriptsPage /></ProtectedRoute>} />
      <Route path="/condition-script-matrix" element={<ProtectedRoute><ConditionScriptMatrixPage /></ProtectedRoute>} />
      <Route path="/script-audit" element={<ProtectedRoute><ScriptAuditPage /></ProtectedRoute>} />
      <Route path="/quote-types-manage" element={<ProtectedRoute><QuoteTypesManagePage /></ProtectedRoute>} />
      <Route path="/token-registry" element={<ProtectedRoute><TokenRegistryPage /></ProtectedRoute>} />
      <Route path="/duplicate-clients" element={<ProtectedRoute><DuplicateClientsPage /></ProtectedRoute>} />
      <Route path="/estimate-templates" element={<ProtectedRoute><EstimateTemplatesPage /></ProtectedRoute>} />
      <Route path="/pdf-viewer/:type/:id" element={<ProtectedRoute><PdfViewerPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

import useFocusFollowScroll from '@/hooks/useFocusFollowScroll';

function AppShell() {
  // v10.26 · Global focus-follow scroll for every text input across the app.
  useFocusFollowScroll();
  return (
    <>
      <AppRoutes />
      <Toaster position="bottom-right" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <IdChipThemeProvider>
          <AppShell />
        </IdChipThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
